import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, ChevronLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TherapistProfile } from "@/types/therapist";
import TherapistList from "@/components/therapist/TherapistList";
import LoadingState from "@/components/therapist/LoadingState";

const Therapists = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [therapists, setTherapists] = useState<TherapistProfile[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>("Loading therapists...");

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
            .maybeSingle();
          
          if (roleData) {
            setUserRole(roleData.role);
          }
        }
        await loadTherapists();
      } catch (error) {
        console.error("Error checking authentication:", error);
        await loadTherapists();
      }
    };
    checkAuth();
  }, [navigate]);

  const setDemoTherapists = () => {
    console.log("Setting demo therapist data");
    const demoTherapists = [{
      id: "demo-therapist-1",
      user_id: "demo-therapist-1",
      first_name: "Dr. Jane",
      last_name: "Smith",
      company: "Mind Wellness Center",
      profile_image_url: null,
      country: "United States",
      language: "English",
      language_secondary: "Spanish",
      profile_bio: "Specialized in cognitive behavioral therapy with 10 years of experience.",
      website_url: "https://example.com/janesmithcbt",
      email: "jane.smith@example.com",
      booking_calendar: null,
      premium: true
    }, {
      id: "demo-therapist-2",
      user_id: "demo-therapist-2",
      first_name: "Dr. Michael",
      last_name: "Johnson",
      company: "Holistic Therapy Practice",
      profile_image_url: null,
      country: "Canada",
      language: "English",
      language_secondary: "French",
      profile_bio: "Focused on trauma recovery and anxiety management using integrative approaches.",
      website_url: "https://example.com/mjtherapy",
      email: "michael.johnson@example.com",
      booking_calendar: null,
      premium: true
    }, {
      id: "demo-therapist-3",
      user_id: "demo-therapist-3",
      first_name: "Dr. Sarah",
      last_name: "Williams",
      company: "Mindfulness & Wellness",
      profile_image_url: null,
      country: "United Kingdom",
      language: "English",
      language_secondary: "",
      profile_bio: "Specializing in mindfulness-based therapy and stress reduction techniques with 8 years of experience.",
      website_url: "https://example.com/sarahwilliams",
      email: "sarah.williams@example.com",
      booking_calendar: null,
      premium: true
    }];
    setTherapists(demoTherapists);
    toast.info("Using demo therapist data for display");
  };

  const loadTherapists = async () => {
    try {
      setLoadingMessage("Loading therapists...");
      console.log("Loading therapists...");
      
      const { data: therapistRoles, error: roleError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'therapist');

      if (roleError) {
        console.error("Error fetching therapist roles:", roleError);
        setDemoTherapists();
        return;
      }

      if (!therapistRoles || therapistRoles.length === 0) {
        console.log("No therapist roles found, using demo data");
        setDemoTherapists();
        return;
      }

      const therapistIds = therapistRoles.map(role => role.user_id);
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, first_name, last_name, company, profile_image_url, country, language, language_secondary, profile_bio, website_url, email, booking_calendar, premium')
        .in('user_id', therapistIds)
        .eq('premium', true);

      if (profileError) {
        console.error("Error fetching user profiles:", profileError);
        setDemoTherapists();
        return;
      }

      if (!profileData || profileData.length === 0) {
        console.log("No profiles found for therapists, using demo data");
        setDemoTherapists();
        return;
      }

      const therapistProfiles = profileData.map(profile => ({
        id: profile.user_id,
        user_id: profile.user_id,
        first_name: profile.first_name || 'Unknown',
        last_name: profile.last_name || 'Therapist',
        company: profile.company || '',
        profile_image_url: profile.profile_image_url,
        country: profile.country || '',
        language: profile.language || '',
        language_secondary: profile.language_secondary || '',
        profile_bio: profile.profile_bio || '',
        website_url: profile.website_url || '',
        email: profile.email || `therapist-${profile.user_id.substring(0, 8)}@example.com`,
        booking_calendar: profile.booking_calendar || null,
        premium: profile.premium || false
      }));

      if (therapistProfiles.length > 0) {
        console.log("Setting therapist profiles:", therapistProfiles.length);
        console.log("Therapist profiles data:", therapistProfiles);
        setTherapists(therapistProfiles);
        toast.success(`Found ${therapistProfiles.length} therapists`);
      } else {
        console.log("No therapist profiles found after mapping");
        setDemoTherapists();
      }
    } catch (error) {
      console.error("Error loading therapists:", error);
      setDemoTherapists();
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate('/auth');
  };

  if (loading) {
    return <LoadingState message={loadingMessage} />;
  }

  return (
    <div className="relative min-h-screen w-full">
      {/* Full-screen blue gradient background */}
      <div className="fixed inset-0 w-full h-full bg-gradient-to-b from-[#E8F0F8] to-white z-0" />
      <div className="relative z-10 min-h-screen w-full">
        <div className="container mx-auto max-w-5xl">
          <Card className="shadow-lg backdrop-blur-sm border border-[#CCCCCC] bg-white">
            <CardHeader className="bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
              <div className="flex items-center space-x-3 justify-between">
                <div className="flex items-center space-x-3">
                  <Users className="h-8 w-8 text-[#3BAFDA]" />
                  <div>
                    <CardTitle className="text-2xl bg-clip-text text-transparent bg-gradient-to-r from-[#333B44] to-[#3BAFDA]">
                      Therapists on Clou
                    </CardTitle>
                    <CardDescription>Browse and connect with therapists</CardDescription>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={goBack} 
                  className="border-[#CCCCCC] hover:bg-[#E8F0F8]"
                  aria-label="Return to previous page"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <TherapistList therapists={therapists} userRole={userRole} />
            </CardContent>
            <CardFooter className="bg-[#E8F0F8]/50 rounded-b-lg border-t border-[#CCCCCC] px-6 py-4 flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Find the right therapist for your needs
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Therapists;
